import React, { useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
interface PendingCancellation {
  id: string;
  transactionId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  amountFCY: string;
  currencyFCY: string;
  currentState: string;
  cancellationReason: string;
  cancellationAction: string;
  requestedBy: string;
  requestedAt: string;
  status: 'PENDING';
}
// Mock data
const MOCK_PENDING_CANCELLATIONS: PendingCancellation[] = Array(10).fill(null).map((_, index) => ({
  id: `CANC-${40000 + index}`,
  transactionId: `TXN-${30000 + index}`,
  mtoName: ['GTBank Ghana', 'Paga Ghana', 'Flutterwave Nigeria'][index % 3],
  payoutType: ['FT', 'IBFT', 'OTC'][index % 3] as 'FT' | 'IBFT' | 'OTC',
  amountFCY: (1000 + index * 100).toFixed(2),
  currencyFCY: 'USD',
  currentState: ['DOWNLOAD', 'SCREENING', 'TITLE_FETCH'][index % 3],
  cancellationReason: ['Duplicate transaction', 'Customer request', 'Incorrect beneficiary details'][index % 3],
  cancellationAction: 'SIMPLE_CANCEL',
  requestedBy: ['Admin User', 'John Doe', 'Jane Smith'][index % 3],
  requestedAt: new Date(Date.now() - index * 3600000).toISOString(),
  status: 'PENDING'
}));
const CheckerCancellationTable: React.FC = () => {
  const [cancellations, setCancellations] = useState<PendingCancellation[]>(MOCK_PENDING_CANCELLATIONS);
  const [selectedCancellation, setSelectedCancellation] = useState<PendingCancellation | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const handleAction = (cancellation: PendingCancellation, action: 'approve' | 'reject') => {
    setSelectedCancellation(cancellation);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedCancellation || !actionType) return;
    if (!reason.trim()) {
      return;
    }
    setIsLoading(true);
    try {
      // Remove from pending list
      setCancellations(prev => prev.filter(c => c.id !== selectedCancellation.id));
      showToast(`Cancellation request ${actionType === 'approve' ? 'approved' : 'rejected'} for ${selectedCancellation.transactionId}`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelectedCancellation(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (cancellation: PendingCancellation) => {
    setSelectedCancellation(cancellation);
    setDetailsDrawerOpen(true);
  };
  const rowActions: RowAction<PendingCancellation>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<PendingCancellation>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-512">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-513">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-514">
          {row.mtoName}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: 'rgba(139, 92, 246, 0.1)',
      color: '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-515" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-516">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'currentState',
    label: 'Current State',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-517" />
  }, {
    id: 'cancellationReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-518">
          {row.cancellationReason}
        </Typography>
  }, {
    id: 'cancellationAction',
    label: 'Action Type',
    renderCell: row => <Typography variant="body2" fontSize="0.75rem" noWrap data-id="element-519">
          {row.cancellationAction.replace(/_/g, ' ')}
        </Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-520">
          {row.requestedBy}
        </Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-521">
          {new Date(row.requestedAt).toLocaleString()}
        </Typography>
  }];
  return <>
      <DataTable title="Pending Cancellation Requests" data={cancellations} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending cancellation requests" initialVisibleColumns={['id', 'transactionId', 'mtoName', 'payoutType', 'amountFCY', 'currentState', 'cancellationReason', 'cancellationAction', 'requestedBy', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-522" />

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-523">
        <DialogTitle data-id="element-524">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-525">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-526" />
            <Typography variant="h6" fontWeight={600} data-id="element-527">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Cancellation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-528">
          <Box sx={{
          mt: 1
        }} data-id="element-529">
            {selectedCancellation && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 1
            }} data-id="element-530">
                  Transaction ID:{' '}
                  <strong data-id="element-531">{selectedCancellation.transactionId}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-532">
                  Maker's Reason:{' '}
                  <strong data-id="element-533">{selectedCancellation.cancellationReason}</strong>
                </Typography>
              </>}

            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this cancellation...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-534" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-535">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-536">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-538" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-539" />} data-id="element-537">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedCancellation && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedCancellation(null);
    }} transaction={{
      id: selectedCancellation.transactionId,
      mtoName: selectedCancellation.mtoName,
      mtoRef: `MTO-${selectedCancellation.transactionId}`,
      bankRef: `BNK-${selectedCancellation.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedCancellation.payoutType,
      amountFCY: selectedCancellation.amountFCY,
      currencyFCY: selectedCancellation.currencyFCY,
      amountPKR: (parseFloat(selectedCancellation.amountFCY) * 15).toString(),
      corridor: 'GH-UK',
      purposeCode: 'FAM',
      purposeText: 'Personal Remittance',
      currentState: selectedCancellation.currentState as any,
      lifecycleStatus: 'FAILED' as any,
      createdAt: selectedCancellation.requestedAt,
      updatedAt: selectedCancellation.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: 'Beneficiary Name',
      beneficiaryIBAN: 'PK00000000000000000000',
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-540" />}
    </>;
};
export default CheckerCancellationTable;